import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, HeartHandshake, Video, ChevronUp, ChevronDown, X, Image as ImageIcon } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";

const SI_MAX_PHOTOS = 5;

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  id: "",
  title: "",
  story: "",
  video_link: "",
  order: 0,
  status: "Active",
  photos: [],
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

export default function SocialImpact() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor"].includes(role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/social-impact");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm({ ...emptyForm, order: items.length }); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      id: p.id,
      title: p.title || "",
      story: p.story || "",
      video_link: p.video_link || "",
      order: p.order || 0,
      status: p.status || "Active",
      photos: Array.isArray(p.photos) ? p.photos.map((ph, i) => ({
        url: ph.url || "", public_id: ph.public_id || "", order: i,
      })) : [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const link = (form.video_link || "").trim();
    if (link && !/^https?:\/\//i.test(link)) return toast.error("Video link must start with http:// or https://");
    const cleanedPhotos = (form.photos || [])
      .filter((p) => (p.url || "").trim())
      .slice(0, SI_MAX_PHOTOS)
      .map((p, i) => ({ url: p.url, public_id: p.public_id || "", order: i }));
    const payload = {
      title: form.title.trim(),
      story: (form.story || "").trim(),
      video_link: link,
      order: Number(form.order) || 0,
      status: form.status === "Inactive" ? "Inactive" : "Active",
      photos: cleanedPhotos,
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/social-impact/${form.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/social-impact", payload);
        toast.success("Added");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/social-impact/${confirmDelete.id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-social-impact">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Social Impact</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Community stories, outreach and social good highlights shown on the public Social Impact page.</div>
        </div>
        {canEdit && (
          <button type="button" onClick={openAdd} data-testid="adm-si-add-btn" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> Add Story
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
          <HeartHandshake size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No stories yet. Add your first Social Impact story.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`adm-si-card-${p.id}`} className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col">
              <div className="aspect-video bg-[#f5f1e8] overflow-hidden relative">
                {(p.photos && p.photos[0] && p.photos[0].url) ? (
                  <img src={p.photos[0].url} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#bcb6aa]"><HeartHandshake size={32} /></div>
                )}
                {(p.photos || []).length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-sm font-semibold">
                    +{(p.photos || []).length - 1} photos
                  </div>
                )}
                {p.video_link && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold inline-flex items-center gap-1"><Video size={10} />Video</div>
                )}
                {p.status !== "Active" && <div className="absolute top-2 left-2 bg-red-100 text-red-700 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold">Inactive</div>}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="font-semibold text-[#1c1a17] leading-snug text-sm">{p.title}</div>
                {p.story && <div className="mt-2 text-xs text-[#5b5650] line-clamp-3">{p.story.replace(/<[^>]*>/g, "")}</div>}
                {canEdit && (
                  <div className="mt-auto pt-3 flex items-center gap-1.5">
                    <button type="button" onClick={() => openEdit(p)} data-testid={`adm-si-edit-${p.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm"><Pencil size={11} />Edit</button>
                    <button type="button" onClick={() => setConfirmDelete(p)} data-testid={`adm-si-delete-${p.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm"><Trash2 size={11} />Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Story" : "Add Social Impact Story"}</DialogTitle>
            <DialogDescription>Community outreach, social work, awareness campaigns and similar impact stories shown on the public site.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *" full>
              <input data-testid="adm-si-input-title" type="text" maxLength={200} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>
            <Field label="Story" full>
              <div data-testid="adm-si-input-story">
                <RichTextEditor
                  value={form.story || ""}
                  onChange={(html) => set("story", html || "")}
                  placeholder="Describe the initiative, the people involved, and the outcome…"
                  minHeight={220}
                />
              </div>
              <div className="mt-1 text-[10px] text-[#8a8478]">Rich text · formatting, links, headings supported</div>
            </Field>
            <Field label={`Photos (up to ${SI_MAX_PHOTOS}, max 200 KB each)`} full>
              <PhotosManager
                photos={form.photos}
                onChange={(next) => set("photos", next)}
              />
            </Field>
            <Field label="Video Link (optional)" full>
              <input data-testid="adm-si-input-video" type="url" maxLength={800} className={inputCls} value={form.video_link} onChange={(e) => set("video_link", e.target.value)} placeholder="e.g. https://youtu.be/…" />
            </Field>
            <Field label="Display Order">
              <input data-testid="adm-si-input-order" type="number" min="0" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} />
            </Field>
            <Field label="Status">
              <select data-testid="adm-si-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="adm-si-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update" : "Add")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete?</AlertDialogTitle>
            <AlertDialogDescription>&ldquo;{confirmDelete?.title}&rdquo; will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="adm-si-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Photos manager — up to 5 photos, 200 KB each, reorderable.
function PhotosManager({ photos, onChange }) {
  const list = photos || [];
  const setAt = (idx, patch) => {
    const next = list.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onChange(next);
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const addSlot = () => {
    if (list.length >= SI_MAX_PHOTOS) return;
    onChange([...list, { url: "", public_id: "", order: list.length }]);
  };

  return (
    <div data-testid="adm-si-photos">
      <div className="grid sm:grid-cols-2 gap-3">
        {list.map((p, i) => (
          <div key={i} data-testid={`adm-si-photo-slot-${i}`} className="bg-[#faf8f2] border border-[#e5e0d8] rounded-sm p-3 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Photo {i + 1}</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`adm-si-photo-up-${i}`} className="text-[#6b655b] hover:text-[#234333] disabled:opacity-30 p-1">
                  <ChevronUp size={14} />
                </button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === list.length - 1} aria-label="Move down" data-testid={`adm-si-photo-down-${i}`} className="text-[#6b655b] hover:text-[#234333] disabled:opacity-30 p-1">
                  <ChevronDown size={14} />
                </button>
                <button type="button" onClick={() => remove(i)} aria-label="Remove" data-testid={`adm-si-photo-remove-${i}`} className="text-red-700 hover:text-red-900 p-1">
                  <X size={14} />
                </button>
              </div>
            </div>
            <FileUploader
              kind="social_impact_photo"
              label=""
              url={p.url}
              onChange={({ url, public_id }) => setAt(i, { url: url || "", public_id: public_id || "" })}
              testid={`adm-si-photo-file-${i}`}
            />
          </div>
        ))}
        {list.length < SI_MAX_PHOTOS && (
          <button type="button" onClick={addSlot} data-testid="adm-si-photo-add" className="border-2 border-dashed border-[#e5e0d8] hover:border-[#234333] hover:bg-[#f5f1e8] rounded-sm min-h-[180px] flex flex-col items-center justify-center gap-2 text-[#6b655b] hover:text-[#234333] transition-colors">
            <ImageIcon size={22} />
            <span className="text-xs uppercase tracking-widest font-semibold">
              Add Photo ({list.length}/{SI_MAX_PHOTOS})
            </span>
            <span className="text-[10px] text-[#8a8478]">Up to 200 KB · JPG / PNG / WEBP</span>
          </button>
        )}
      </div>
      {list.length > 0 && (
        <div className="mt-2 text-[11px] text-[#6b655b]">
          {list.length} of {SI_MAX_PHOTOS} photos attached · use arrows to reorder
        </div>
      )}
    </div>
  );
}
